import React, {useState, useEffect} from "react";
import { useTranslation } from "react-i18next";

// Props Type
type GameActionButtonsProps = {
  gameTitle: string;
  gameId: string;
};

const GameActionButtons: React.FC<GameActionButtonsProps> = ({ gameTitle, gameId }) => {
  const { t } = useTranslation();

  const handleShare = () => {
    const shareData = {
      title: gameTitle || t("game.defaultTitle"),
      text: t("game.shareText"),
      url: window.location.href,
    };

    if (navigator.share) {
      navigator
        .share(shareData)
        .catch((err) => console.error("Share failed:", err));
    } else {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => alert(t("game.linkCopied")))
        .catch(() => alert(t("game.copyFailed")));
    }
  };
 const [likeCount, setLikeCount] = useState<number>(0);
  const [dislikeCount, setDislikeCount] = useState<number>(0);

  // Utility to generate consistent hash from game ID
  const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  };

  const getRandomFromSeed = (seed: number, min: number, max: number) => {
    const pseudoRandom = (seed % 1000) / 1000; // between 0-1
    return parseFloat((min + pseudoRandom * (max - min)).toFixed(1));
  };

  useEffect(() => {
    const seed = hashString(gameId);
    const like = getRandomFromSeed(seed, 1.5, 3.5);
    const dislike = getRandomFromSeed(seed + 1, 0.3, 2.0);
    setLikeCount(like);
    setDislikeCount(dislike);
  }, [gameId]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 mt-2 text-gray-400 text-sm px-4">
      <h2 className="text-white text-lg font-semibold">{gameTitle}</h2>

      <div className="flex flex-wrap items-center gap-6">
        {/* Like */}
        <button className="flex items-center gap-2 hover:text-blue-500">
          {/* SVG... */}
           <svg xmlns="http://www.w3.org/2000/svg" width="21" height="19" fill="none" viewBox="0 0 21 19" className="transform rotate-180">
                        <path fill="#999" d="M2.472 12.167q-.714 0-1.26-.548-.548-.547-.548-1.26V8.744q0-.158.04-.338t.09-.336l2.867-6.762q.215-.48.72-.81A1.9 1.9 0 0 1 5.437.166h10.516v12l-5.818 5.767a1.2 1.2 0 0 1-.69.355 1.2 1.2 0 0 1-.752-.134 1.18 1.18 0 0 1-.527-.546 1.06 1.06 0 0 1-.071-.733l1.077-4.71zm11.98-.639V1.667H5.434q-.105 0-.216.057a.37.37 0 0 0-.168.193l-2.885 6.75v1.692a.3.3 0 0 0 .087.221.3.3 0 0 0 .22.086h8.597l-1.254 5.481zM18.857.167q.746 0 1.277.53.531.532.531 1.277v8.385q0 .745-.53 1.277a1.74 1.74 0 0 1-1.278.53h-2.903v-1.5h2.903a.3.3 0 0 0 .222-.086.3.3 0 0 0 .086-.221V1.974a.3.3 0 0 0-.086-.221.3.3 0 0 0-.222-.086h-2.903v-1.5z" />
                    </svg>
          <span className="text-base text-gray-400">{likeCount}k</span>
        </button>

        {/* Dislike */}
        <button className="flex items-center gap-2 hover:text-red-500">
           <svg xmlns="http://www.w3.org/2000/svg" width="21" height="19" fill="none" viewBox="0 0 21 19">
                        <path fill="#999" d="M2.472 12.167q-.714 0-1.26-.548-.548-.547-.548-1.26V8.744q0-.158.04-.338t.09-.336l2.867-6.762q.215-.48.72-.81A1.9 1.9 0 0 1 5.437.166h10.516v12l-5.818 5.767a1.2 1.2 0 0 1-.69.355 1.2 1.2 0 0 1-.752-.134 1.18 1.18 0 0 1-.527-.546 1.06 1.06 0 0 1-.071-.733l1.077-4.71zm11.98-.639V1.667H5.434q-.105 0-.216.057a.37.37 0 0 0-.168.193l-2.885 6.75v1.692a.3.3 0 0 0 .087.221.3.3 0 0 0 .22.086h8.597l-1.254 5.481zM18.857.167q.746 0 1.277.53.531.532.531 1.277v8.385q0 .745-.53 1.277a1.74 1.74 0 0 1-1.278.53h-2.903v-1.5h2.903a.3.3 0 0 0 .222-.086.3.3 0 0 0 .086-.221V1.974a.3.3 0 0 0-.086-.221.3.3 0 0 0-.222-.086h-2.903v-1.5z" />
                    </svg>
          <span className="text-base text-gray-400">{dislikeCount}k</span>
           
        </button>

        {/* Heart */}
        <button className="hover:text-pink-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="17" fill="none" viewBox="0 0 20 17">
                        <path fill="#999" d="M10.01 16.71q.321 0 .645-.116a1.5 1.5 0 0 0 .57-.361l1.437-1.306a66 66 0 0 0 4.748-4.764q2.09-2.34 2.09-5.013 0-2.13-1.436-3.564Q16.629.15 14.5.15q-1.21 0-2.39.558-1.18.557-2.11 1.811Q9.07 1.265 7.89.708A5.54 5.54 0 0 0 5.5.15q-2.13 0-3.564 1.436Q.5 3.022.5 5.15q0 2.701 2.125 5.068a64 64 0 0 0 4.739 4.724l1.42 1.29q.247.247.575.362.33.115.65.115m.71-12.671q.81-1.237 1.708-1.813T14.5 1.65q1.5 0 2.5 1t1 2.5q0 1.204-.776 2.517a16.3 16.3 0 0 1-1.948 2.612 36 36 0 0 1-2.54 2.536q-1.367 1.239-2.534 2.302a.3.3 0 0 1-.202.077.3.3 0 0 1-.202-.077q-1.167-1.063-2.535-2.302a36 36 0 0 1-2.539-2.536 16.3 16.3 0 0 1-1.948-2.612Q2 6.354 2 5.15q0-1.5 1-2.5t2.5-1q1.175 0 2.072.576t1.709 1.813a.8.8 0 0 0 .319.288.89.89 0 0 0 .8 0 .8.8 0 0 0 .32-.288"/>
                    </svg>
        </button>

        {/* Share */}
        <button className="hover:text-green-500" onClick={handleShare}>
          <svg width="18" height="20" fill="currentColor" viewBox="0 0 18 20">
                        <path d="M14.81 19.5a2.6 2.6 0 0 1-1.91-.78 2.6 2.6 0 0 1-.78-1.91q0-.15.1-.73L5.11 11.9q-.36.37-.86.59a2.7 2.7 0 0 1-1.07.21 2.58 2.58 0 0 1-1.9-.79A2.6 2.6 0 0 1 .5 10q0-1.12.78-1.9a2.58 2.58 0 0 1 1.9-.79q.57 0 1.07.21t.86.59L12.32 4.96a2 2 0 0 1-.08-.36 3 3 0 0 1-.02-.38q0-1.12.79-1.91A2.6 2.6 0 0 1 14.81.5a2.6 2.6 0 0 1 1.91.79q.79.79.79 1.91T16.71 5.1a2.6 2.6 0 0 1-1.91.79 2.7 2.7 0 0 1-1.06-.22 2.7 2.7 0 0 1-.85-.59L5.78 9.26q.06.18.08.36.02.18.02.38t-.02.38a2 2 0 0 1-.08.36l7.11 4.18q.36-.38.85-.59a2.6 2.6 0 0 1 1.06-.22q1.12 0 1.91.79a2.6 2.6 0 0 1 .79 1.91 2.6 2.6 0 0 1-.79 1.91 2.6 2.6 0 0 1-1.91.79Z" />
                    </svg>
        </button>

        {/* Fullscreen */}
        <FullscreenButton />
      </div>
    </div>
  );
};

const FullscreenButton: React.FC = () => {
  const toggleFullscreen = () => {
    const el = document.getElementById("game-preview");
    if (!el) return;

    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {
        el.classList.toggle("fullscreen-mode");
      });
    } else {
      el.classList.toggle("fullscreen-mode");
    }
  };

  return (
    <button className="hover:text-yellow-500" onClick={toggleFullscreen}>
     <svg width="18" height="18" fill="currentColor" viewBox="0 0 18 18">
                <path d="M2 16h2.46q.32 0 .54.21.21.22.21.54t-.22.54q-.22.21-.53.21H1.4a.87.87 0 0 1-.64-.26.87.87 0 0 1-.26-.64v-3.06q0-.32.22-.54.21-.21.53-.21.32 0 .54.21.21.22.21.54zm14.01 0v-2.46q0-.32.22-.54.22-.21.54-.21.32 0 .54.21.21.22.21.54v3.06q0 .38-.26.64a.88.88 0 0 1-.64.26h-3.06a.73.73 0 0 1-.53-.22q-.22-.21-.22-.53 0-.32.22-.54.21-.21.53-.21zM2 2v2.46q0 .32-.22.54-.21.21-.53.21-.32 0-.54-.21Q.5 4.78.5 4.46V1.4q0-.38.26-.64.26-.26.64-.26h3.06q.32 0 .54.22.21.21.21.53 0 .32-.21.54-.22.21-.54.21zm14.01 0h-2.46a.73.73 0 0 1-.53-.22q-.22-.21-.22-.53 0-.32.22-.54.21-.21.53-.21h3.06q.38 0 .64.26.26.26.26.64v3.06q0 .32-.22.54-.21.21-.53.21-.32 0-.54-.21-.21-.22-.21-.54z" />
            </svg>
    </button>
  );
};

export default GameActionButtons;
